<template>
  <section class='section'>
    <form-tracking-modal
      v-if="trackingLead"
      form-type="landing"
      :record-id="trackingLead.id"
      :record-label="trackingLeadLabel"
      :close="closeTrackingModal"
      :action="saveTracking"
    />

    <modal-groups
      v-if="modalGroups"
      :close="showModal"
      :userGroupsList="userGroupsList"
      :createUserGroup="createUserGroup"
      :loadingAdd="loadingAdd"
    />
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <h1>Leads</h1>
        </div>
      </div>
      <div class="level-left">
      </div>
      <div style="display: flex; flex-wrap: wrap; gap: 10px; padding: 10px 0; justify-content: end;">
        <div class="level-item">
          <button
            class="button"
            type="button"
            :class="{ 'is-loading': isLoading }"
            @click="getCsvDownload"
          >
            Descargar
          </button>
        </div>
        <div class="level-item">
          <div class="control">
            <input class="input" style="max-width: 150px;" type="text" v-model="search" @keyup="searchLeads" placeholder="Buscador...">
          </div>
        </div>
        <div class="level-item">
          <div class="select">
            <select v-model="status" @change="updateFilters({ status: status, page: 1 })">
              <option value="">Todos los estados</option>
              <option value="sin-estado">Sin estado</option>
              <option
                v-for="option in statusOptions"
                :key="option.slug"
                :value="option.slug"
              >
                {{ option.nombre }}
              </option>
            </select>
          </div>
        </div>
        <div class="level-item">
          <div class="select">
            <select v-model="assignedAdminId" @change="updateFilters({ assignedAdminId: assignedAdminId, page: 1 })">
              <option value="">Todos los responsables</option>
              <option value="sin-responsable">Sin responsable</option>
              <option
                v-for="admin in admins"
                :key="admin.id"
                :value="String(admin.id)"
              >
                {{ admin.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="level-item">
          <div class="select">
            <select v-model="contacted" @change="updateFilters({ contacted: contacted, page: 1 })">
              <option value="">Contactado: todos</option>
              <option value="si">Contactado: sí</option>
              <option value="no">Contactado: no</option>
            </select>
          </div>
        </div>
        <div class="level-item">
          <button @click="toggleDatePicker" v-if="!showDatePicker" class="button">
            Filtrar por fecha
          </button>
          <div v-else style="max-width: 150px; overflow: hidden;">
            <v-date-picker
              mode="range"
              v-model="date"
              :max-date="today"
              @input="onDateChange"
            />
          </div>
          <button @click="resetDate" v-if="showDatePicker" class="button" style="padding: 4px;">
            <span class="material-icons">
              restart_alt
            </span>
          </button>
        </div>
      </div>
    </nav>
    <div class="columns is-multiline">
      <div class="column is-12">
        <base-table
          class="is-bordered is-striped is-narrow is-fullwidth is-hoverable bankingTable"
          :columns="columnsData"
          :data="leadsList"
          :loading=loading
        >
        <div
          slot="responsable"
          slot-scope="scope"
        >
          <span
            class="material-icons contacted-icon"
            :class="scope.row.isAdminContacted ? 'has-text-success' : 'has-text-grey-light'"
            :title="scope.row.isAdminContacted ? 'Contactado' : 'Sin contactar'"
          >
            {{ scope.row.isAdminContacted ? 'check_circle' : 'radio_button_unchecked' }}
          </span>
          <span class="has-tooltip-top" :data-tooltip="ownerTooltip(scope.row)">
            {{ leadOwner(scope.row) || 'Sin asignar' }}
          </span>
        </div>
        <div
          slot="estado"
          slot-scope="scope"
          class="estado-cell"
        >
          <span class="tag" :class="statusColor(scope.row.trackingStatus)">
            {{ statusLabel(scope.row.trackingStatus) || 'Sin estado' }}
          </span>
          <button class="button is-small is-info is-light" @click="openTrackingModal(scope.row)">
            Editar
          </button>
        </div>
        <div id="urlLanding" slot="urlLanding" slot-scope="scope">
          <span style="margin-right: 10px;">{{ truncateUrl(scope.row.urlLanding, 20) }}</span>
          <button
            v-if="scope.row.urlLanding"
            class="button is-small is-info"
            @click="copyToClipboard(scope.row.urlLanding)"
          >Copiar</button>
        </div>
        </base-table>
      </div>
    </div>
    <base-pagination
      class="is-medium"
      :page="page"
      :pages="parseInt(pages)"
      :action="paginator"
      :loading="loading"
    />
  </section>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import moment from 'moment'
import mixin from '../../mixin/validateDate'
import { trackingStatusLabel, trackingStatusColor, TRACKING_STATUS_OPTIONS } from '@/lib/formTracking'

export default {
  mixins: [mixin],
  name: 'Leads',
  components: {
    BaseTable: () => import('@/components/Table'),
    BasePagination: () => import('@/components/BasePagination'),
    ModalGroups: () => import('@/components/Customers/ModalGroups'),
    FormTrackingModal: () => import('@/components/Leads/FormTrackingModal')
  },
  data () {
    return {
      search: '',
      page: 1,
      pages: '',
      startDate: '',
      endDate: '',
      status: '',
      assignedAdminId: '',
      contacted: '',
      date: {},
      today: new Date(),
      showDatePicker: false,
      loading: false,
      isLoading: false,
      leadsList: [],
      admins: [],
      statusOptions: TRACKING_STATUS_OPTIONS,
      columnsData: [
        {
          id: 'id',
          header: 'Id',
          class: 'column-mobile'
        },
        { id: 'responsable', name: 'responsable', header: 'Responsable', class: 'column-mobile has-text-centered' },
        { id: 'estado', name: 'estado', header: 'Estado', class: 'column-mobile has-text-centered' },
        { id: 'nombreUsuario', header: 'Nombre usuario', class: 'column-mobile' },
        { id: 'correo', header: 'Correo', class: 'column-mobile' },
        { id: 'telefono', header: 'Télefono', class: 'column-mobile' },
        {
          id: 'fechaCreacion',
          header: 'Fecha',
          class: 'column-mobile',
          accessor: (row) => moment(row.fechaCreacion).format('DD-MM-YYYY')
        },
        { id: 'modelo', header: 'Modelo', class: 'column-mobile' },
        { id: 'marca', header: 'Marca', class: 'column-mobile' },
        { id: 'almacenamiento', header: 'Almacenamiento', class: 'column-mobile' },
        { id: 'color', header: 'Color', class: 'column-mobile' },
        { id: 'ciudad', header: 'Ciudad', class: 'column-mobile' },
        {
          id: 'precio',
          header: 'Precio',
          class: 'column-mobile',
          accessor: (row) => (row.precio === null || row.precio === undefined) ? '' : `$${row.precio}`
        },
        { id: 'urlLanding', name: 'urlLanding', header: 'Landing', class: 'column-mobile' }
      ],
      modalGroups: false,
      userGroupsList: [],
      loadingAdd: false,
      trackingLead: null
    }
  },
  computed: {
    trackingLeadLabel () {
      if (!this.trackingLead) return ''
      const { id, nombreUsuario, modelo } = this.trackingLead
      return [`#${id}`, nombreUsuario, modelo].filter(Boolean).join(' · ')
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler (query) {
        this.setFiltersFromQuery(query)
        this.fetchLeads()
      }
    }
  },
  async mounted () {
    this.admins = await this.getAdminOptions()
  },
  methods: {
    ...mapMutations(['setAuthenticationValue']),
    ...mapActions(['getLeads', 'csvDownloadLeads', 'addTemporalNotification', 'upsertFormTracking', 'getAdminOptions']),
    statusLabel: trackingStatusLabel,
    statusColor: trackingStatusColor,
    // Responsable y "admin que contactó" son lo mismo: los leads viejos solo
    // tienen el segundo, así que sirve de respaldo hasta que se edite el lead.
    leadOwner (row) {
      return row.trackingAdminName || row.adminName || ''
    },
    ownerTooltip (row) {
      const owner = this.leadOwner(row)
      return owner
        ? `Responsable: ${owner} (se cambia desde Editar)`
        : 'Sin responsable asignado (se asigna desde Editar)'
    },
    parseLocalDate (dateString) {
      if (!dateString) return null
      const [y, m, d] = dateString.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
    setFiltersFromQuery (query = {}) {
      this.page = parseInt(query.page, 10) || 1
      this.search = query.search || ''
      this.startDate = query.startDate || ''
      this.endDate = query.endDate || ''
      this.status = query.status || ''
      this.assignedAdminId = query.assignedAdminId || ''
      this.contacted = query.contacted || ''

      this.date = {
        start: this.parseLocalDate(this.startDate),
        end: this.parseLocalDate(this.endDate)
      }

      this.showDatePicker = !!(this.startDate && this.endDate)
    },
    currentFilters () {
      return {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        status: this.status,
        assignedAdminId: this.assignedAdminId,
        contacted: this.contacted
      }
    },
    updateFilters (newFilters = {}) {
      const query = {
        page: this.page,
        ...this.currentFilters(),
        ...newFilters
      }

      this.$router.push({ name: 'leads', query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') console.error(err)
      })
    },
    fetchLeads () {
      this.loading = true
      return this.getLeads({ page: this.page, ...this.currentFilters() })
        .then(resp => {
          this.loading = false
          if (!resp) return
          this.leadsList = resp.payload
          this.pages = resp.totalPages
        })
    },
    paginator (page) {
      this.updateFilters({ page })
    },
    searchLeads ({ target }) {
      if (target.value.length >= 3 || target.value.length === 0) {
        this.updateFilters({ search: target.value, page: 1 })
      }
    },
    onDateChange (newDate) {
      if (newDate == null) return null

      this.startDate = moment(newDate.start).format('YYYY-MM-DD')
      this.endDate = moment(newDate.end).format('YYYY-MM-DD')

      this.updateFilters({ page: 1 })
    },
    toggleDatePicker () {
      this.showDatePicker = !this.showDatePicker
    },
    resetDate () {
      this.date = {}
      this.showDatePicker = false
      this.startDate = ''
      this.endDate = ''

      this.updateFilters({ page: 1 })
    },
    showModal () {
      this.modalGroups = !this.modalGroups
    },
    getCsvDownload () {
      this.isLoading = true
      this.csvDownloadLeads(this.currentFilters())
        .then(resp => {
          if (resp) {
            const exportName = 'leads.csv'
            const blob = new Blob([resp], { type: 'text/csv;charset=utf-8;' })
            if (navigator.msSaveBlob) {
              navigator.msSaveBlob(blob, exportName)
            } else {
              const link = document.createElement('a')
              if (link.download !== undefined) {
                const url = URL.createObjectURL(blob)
                link.setAttribute('href', url)
                link.setAttribute('download', exportName)
                link.style.visibility = 'hidden'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
              }
            }
          } else {
            this.addTemporalNotification({ type: 'danger', message: 'Error en la descarga' })
          }
          this.isLoading = false
        })
    },
    openTrackingModal (row) {
      this.trackingLead = row
    },
    closeTrackingModal () {
      this.trackingLead = null
    },
    async saveTracking (data) {
      const saved = await this.upsertFormTracking({
        formType: 'landing',
        recordId: this.trackingLead.id,
        data
      })

      if (saved) this.fetchLeads()

      return saved
    },
    copyToClipboard (text) {
      if (!text) return
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.addTemporalNotification({ type: 'success', message: 'Texto copiado al portapapeles' })
    },
    truncateUrl (url, length) {
      if (!url) return '-'
      return url.length > length ? url.substring(0, length) + '...' : url
    }
  }
}
</script>

<style scoped>
h2 {
  margin-top: 40px;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 700;
}
.section {
  width: 100%;
  padding: 0 20px 0 20px;
}
.estado-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.contacted-icon {
  font-size: 16px;
  vertical-align: text-bottom;
  margin-right: 3px;
}
</style>
